import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Chip, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { LinkedinTypes } from 'enums';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { LinkedLicenseSummary } from 'types/licenses.types';
import AccountItemMenu from './account-item-menu';
import LinkedinAccountStatus from './linkedin-account-status';
import { LinkedinAccount, LinkedinAccountStatuses } from './linkedin-accounts.types';
import { activeRiskReductionUntil, riskReductionPercent } from './risk-reduction';
import ScheduledLogoutDialog from './scheduled-logout-dialog';
import { formatDateTime } from 'utils/date.utils';

/** Seat warning wording per product — Sales Navigator and Recruiter both have a seat. */
const seatLabelKey = (product?: string): string =>
  (product === 'recruiter' ? 'recruiterSeatNotAnswering' : 'salesNavSeatNotAnswering');
const seatHintKey = (product?: string): string =>
  (product === 'recruiter' ? 'recruiterSeatNotAnsweringHint' : 'salesNavSeatNotAnsweringHint');
const seatActionKey = (product?: string): string =>
  (product === 'recruiter' ? 'recruiterSeatReLogin' : 'salesNavSeatReLogin');

const PRODUCT_LABELS: Record<string, string> = {
  saia_recruiting: 'SAIA Recruiting',
  saia_sales: 'SAIA Sales',
};

interface Props {
  account: LinkedinAccount;
  linkedLicenses: LinkedLicenseSummary[];
  syncLoading: boolean;
  logoutLoading: boolean;

  handleOpenAppSyncAndSynchronizeLiAccount(account: LinkedinAccount): () => void;

  handleOpenAppSyncAndCorrectAccessData(account: LinkedinAccount): () => void;

  handleOpenRecruiterParallelLoginDialog(account: LinkedinAccount): () => void;

  handleOpenRemoveLinkedinAccountDialog?(account: LinkedinAccount): () => void;

  handleOpenEditLinkedinAccountGeneralSettingsDialog(account: LinkedinAccount): () => void;

  handleOpenEditLinkedinAccountLoginInformationDialog(account: LinkedinAccount): () => void;

  handleOpenEditEmailCredentialsDialog(account: LinkedinAccount): () => void;

  handleOpenManageEmailSendersDialog?(account: LinkedinAccount): () => void;

  handleOpenEditSignatureDialog?(account: LinkedinAccount): () => void;

  handleOpenAppSyncAndVerifyPin(account: LinkedinAccount): () => void;

  handleOpenAppSyncAndSolveCaptcha(account: LinkedinAccount): () => void;

  handleOpenAppSyncThirdPartyWebsite(account: LinkedinAccount): () => void;

  handleOpenEditLinkedinAccountNameDialog?(account: LinkedinAccount): () => void;

  // NEW: open limits & schedule dialog
  handleOpenEditLimitsDialog?(account: LinkedinAccount): () => void;
  // Open the license assignment dialog
  handleOpenAssignLicenseDialog?(account: LinkedinAccount): () => void;
  handleLogoutLinkedinAccount(account: LinkedinAccount, stayLoggedOutTill?: number): () => void;

  /** Soft cancel of a stuck login (reset), as opposed to the device logout above. */
  handleCancelLinkedinLogin(account: LinkedinAccount): () => void;

  // NEW: open reset local storage dialog
  handleOpenResetLocalStorageDialog?(account: LinkedinAccount): () => void;
  handleOpenAccountConnectionsDialog?(account: LinkedinAccount): () => void;

  handleOpenOnboardingIncompleteDialog(account: LinkedinAccount): () => void;

  handleOpenFairUsePauseDialog(): void;

  handleOpenTwoFaNotEnabledDialog(account: LinkedinAccount): () => void;

  generateShareableEditPath?(accountId: string): () => void;
}

const LinkedinAccountItem: React.FC<Props> = ({
  account,
  linkedLicenses,
  syncLoading,
  logoutLoading,
  handleOpenAppSyncAndSynchronizeLiAccount,
  handleOpenAppSyncAndCorrectAccessData,
  handleOpenRecruiterParallelLoginDialog,
  handleOpenRemoveLinkedinAccountDialog,
  handleOpenEditLinkedinAccountGeneralSettingsDialog,
  handleOpenEditLinkedinAccountLoginInformationDialog,
  handleOpenEditEmailCredentialsDialog,
  handleOpenManageEmailSendersDialog,
  handleOpenEditSignatureDialog,
  handleOpenAppSyncAndVerifyPin,
  handleOpenAppSyncAndSolveCaptcha,
  handleOpenAppSyncThirdPartyWebsite,
  handleOpenEditLinkedinAccountNameDialog,
  handleOpenEditLimitsDialog,
  handleOpenAssignLicenseDialog,
  handleLogoutLinkedinAccount,
  handleCancelLinkedinLogin,
  handleOpenResetLocalStorageDialog,
  handleOpenAccountConnectionsDialog,
  handleOpenOnboardingIncompleteDialog,
  handleOpenFairUsePauseDialog,
  handleOpenTwoFaNotEnabledDialog,
  generateShareableEditPath,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const profilesCrawledToday = account.profilesCrawledToday ?? account.usedToday?.visits;
  const profilesCrawledTodayLimit =
    account.profilesCrawledTodayLimit ?? account.todayLimits?.visits ?? account.visitDailyLimit;
  const hasUsageData = profilesCrawledToday !== undefined;
  const formatRatio = (used?: number, limit?: number | null) =>
    limit !== undefined && limit !== null ? `${used ?? 0}/${limit}` : `${used ?? 0}`;

  const [scheduledLogoutDialog, setScheduledLogoutDialog] = useState<{ open: boolean; selectedDuration: string }>({
    open: false,
    selectedDuration: '1800',
  });

  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenMenu = (el: any) => {
    setMenuOpen(true);
    setAnchorEl(el);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

  /**
   * Logout dialog handlers
   */

  const handleOpenScheduledLogoutDialog = () => {
    setScheduledLogoutDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseScheduledLogoutDialog = () => {
    setScheduledLogoutDialog({ open: false, selectedDuration: '1800' });
  };

  const handleSelectedDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setScheduledLogoutDialog((prev) => ({ ...prev, selectedDuration: event.target.value }));
  };

  const handleConfirmLogout = () => {
    const { selectedDuration } = scheduledLogoutDialog;
    const durationInSeconds = parseInt(selectedDuration, 10);

    handleLogoutLinkedinAccount(account, durationInSeconds)();
    handleCloseScheduledLogoutDialog();
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Stack direction={'row'} gap={1} alignItems="center">
            {account.firstName || account.lastName
              ? `${account.firstName || ''} ${account.lastName || ''}`.trim()
              : (account.linkedinEmail || account.userEmail || '-')}
            {!account.firstName || !account.lastName ? <PaiHelpIcon tooltipText={t('namesNotFoundWarning')} variant={'warning'} /> : null}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap title={account.linkedinEmail || account.userEmail}>
            {account.linkedinEmail || account.userEmail || '-'}
          </Typography>
        </TableCell>
        <TableCell>
          {(account.liProductType === LinkedinTypes.LINKEDIN || (account.liProductType as unknown as string) === 'normal')
            ? 'LinkedIn'
            : account.liProductType === LinkedinTypes.SALES_NAV
            ? 'LinkedIn + Sales Navigator'
            : account.liProductType === LinkedinTypes.RECRUITER
            ? 'LinkedIn + Recruiter'
            : null}
        </TableCell>
        <TableCell>
          {linkedLicenses.length > 0 ? (
            <Stack direction={'row'} spacing={0.5} flexWrap="wrap">
              {linkedLicenses.map((lic) => (
                <Chip
                  key={lic._id}
                  label={PRODUCT_LABELS[lic.product] || lic.product}
                  size="small"
                  color={lic.isActive && !lic.isExpired ? 'success' : 'error'}
                  variant="outlined"
                />
              ))}
            </Stack>
          ) : (
            handleOpenAssignLicenseDialog ? (
              <PaiHelpIcon tooltipText={t('noLicense')} variant="warning" />
            ) : (
              <Typography variant="body2" color="text.secondary">{t('noLicense')}</Typography>
            )
          )}
        </TableCell>
        <TableCell>
          <Stack direction={'row'} spacing={1} alignItems="center">
            {(() => {
              const getAlias = () => {
                for (const ea of account.emailAccounts ?? []) {
                  const a = (ea as any).username || (ea as any).smtp?.username || (ea as any).oauth?.email || (ea as any).gmailOauth?.email;
                  if (a) return a;
                }
                return account.emailConfig?.smtp?.username || account.emailConfig?.oauth?.email || account.emailConfig?.gmailOauth?.email || '-';
              };
              const alias = getAlias();
              return <Typography>{alias}</Typography>;
            })()}
            {account.emailConfig?.accessRevoked && <PaiHelpIcon tooltipText={t('emailAccessRevoked')} variant={'warning'} />}
            {handleOpenManageEmailSendersDialog && (
              <IconButton size="small" color="primary" onClick={handleOpenManageEmailSendersDialog(account)}>
                <AddCircleOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack spacing={0.5}>
            <LinkedinAccountStatus
              status={account.status}
              syncLoading={syncLoading}
              logoutLoading={logoutLoading}
              handleOpenAppSyncAndSynchronizeLiAccount={handleOpenAppSyncAndSynchronizeLiAccount(account)}
              handleOpenAppSyncAndCorrectAccessData={handleOpenAppSyncAndCorrectAccessData(account)}
              handleLogoutLinkedinAccount={handleOpenScheduledLogoutDialog}
              handleCancelLinkedinLogin={handleCancelLinkedinLogin(account)}
              handleOpenAppSyncAndVerifyPin={handleOpenAppSyncAndVerifyPin(account)}
              handleOpenAppSyncAndSolveCaptcha={handleOpenAppSyncAndSolveCaptcha(account)}
              handleOpenAppSyncThirdPartyWebsite={handleOpenAppSyncThirdPartyWebsite(account)}
              handleOpenRecruiterParallelLoginDialog={handleOpenRecruiterParallelLoginDialog(account)}
              handleOpenOnboardingIncompleteDialog={handleOpenOnboardingIncompleteDialog(account)}
              handleOpenFairUsePauseDialog={handleOpenFairUsePauseDialog}
              handleOpenTwoFaNotEnabledDialog={handleOpenTwoFaNotEnabledDialog(account)}
            />
            {account.status === LinkedinAccountStatuses.SYNCHRONIZED && account.seatAccess?.ok === false ? (
              <>
                <Tooltip title={t(seatHintKey(account.seatAccess.product))} arrow>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <WarningAmberOutlinedIcon fontSize="small" color="warning" />
                    <Typography variant="caption" color="warning.main">
                      {t(seatLabelKey(account.seatAccess.product))}
                    </Typography>
                  </Stack>
                </Tooltip>
                <PaiLink
                  text={t(seatActionKey(account.seatAccess.product))}
                  size="sm"
                  disabled={syncLoading || logoutLoading}
                  action={handleOpenAppSyncAndSynchronizeLiAccount(account)}
                />
              </>
            ) : null}
            {activeRiskReductionUntil(account) ? (
              <Tooltip
                title={t('riskReductionHint', {
                  date: formatDateTime(activeRiskReductionUntil(account)!.toISOString(), 'P'),
                  percent: riskReductionPercent(account),
                })}
                arrow
              >
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <WarningAmberOutlinedIcon fontSize="inherit" color="warning" />
                  <Typography variant="caption" color="warning.main">
                    {t('riskReductionLabel', {
                      date: formatDateTime(activeRiskReductionUntil(account)!.toISOString(), 'P'),
                      percent: riskReductionPercent(account),
                    })}
                  </Typography>
                </Stack>
              </Tooltip>
            ) : null}
            {hasUsageData ? (
              <Typography variant="caption" color="text.secondary">
                {t('linkedinAccountTodayUsage', {
                  profiles: formatRatio(profilesCrawledToday, profilesCrawledTodayLimit),
                })}
              </Typography>
            ) : null}
          </Stack>
        </TableCell>
        <TableCell>
          <IconButton size={'small'} onClick={(event: React.MouseEvent<HTMLElement>) => handleOpenMenu(event.currentTarget)} disabled={syncLoading}>
            <MoreVertIcon className={classes.icon} />
          </IconButton>
        </TableCell>
      </TableRow>

      <AccountItemMenu
        anchorEl={anchorEl}
        menuOpen={menuOpen}
        handleOpenEditLinkedinAccountGeneralSettingsDialog={handleOpenEditLinkedinAccountGeneralSettingsDialog(account)}
        handleOpenEditLinkedinAccountLoginInformationDialog={handleOpenEditLinkedinAccountLoginInformationDialog(account)}
        handleOpenManageEmailSendersDialog={handleOpenManageEmailSendersDialog ? handleOpenManageEmailSendersDialog(account) : undefined}
        handleOpenEditEmailCredentialsDialog={handleOpenEditEmailCredentialsDialog(account)}
        handleOpenEditSignatureDialog={!_.isNil(handleOpenEditSignatureDialog) ? handleOpenEditSignatureDialog(account) : undefined}
        generateShareableEditPath={!_.isNil(generateShareableEditPath) ? generateShareableEditPath(account._id) : undefined}
        handleOpenEditLimitsDialog={handleOpenEditLimitsDialog ? handleOpenEditLimitsDialog(account) : undefined}
        handleOpenAssignLicenseDialog={handleOpenAssignLicenseDialog ? handleOpenAssignLicenseDialog(account) : undefined}
        handleOpenResetLocalStorageDialog={handleOpenResetLocalStorageDialog ? handleOpenResetLocalStorageDialog(account) : undefined}
        handleOpenAccountConnectionsDialog={handleOpenAccountConnectionsDialog ? handleOpenAccountConnectionsDialog(account) : undefined}
        handleOpenRemoveLinkedinAccountDialog={
          !_.isNil(handleOpenRemoveLinkedinAccountDialog) ? handleOpenRemoveLinkedinAccountDialog(account) : undefined
        }
        handleOpenEditLinkedinAccountNameDialog={
          !_.isNil(handleOpenEditLinkedinAccountNameDialog) ? handleOpenEditLinkedinAccountNameDialog(account) : undefined
        }
        handleLogin={account.status === LinkedinAccountStatuses.NOT_SYNCHRONIZED ? handleOpenAppSyncAndSynchronizeLiAccount(account) : undefined}
        handleLogout={account.status === LinkedinAccountStatuses.SYNCHRONIZED ? handleOpenScheduledLogoutDialog : undefined}
        handleCloseMenu={handleCloseMenu}
      />
      <ScheduledLogoutDialog
        open={scheduledLogoutDialog.open}
        loading={logoutLoading}
        selectedDuration={scheduledLogoutDialog.selectedDuration}
        handleSelectedDurationChange={handleSelectedDurationChange}
        handleClose={handleCloseScheduledLogoutDialog}
        handleConfirmLogout={handleConfirmLogout}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  icon: {
    fontSize: theme.typography.pxToRem(18),
    '&.success': {
      color: theme.palette.success.main,
    },
    '&.error': {
      color: theme.palette.error.main,
    },
    '&.warning': {
      color: theme.palette.warning.main,
    },
  },
}));

export default LinkedinAccountItem;
